import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Phone, Mail, ShieldCheck, CheckCircle2, ArrowRight, Loader2, MapPin, Clock, ChevronDown, X, ChevronLeft, ChevronRight, Briefcase } from 'lucide-react'
import ContactForm from '../components/ContactForm.jsx'
import Gallery from '../components/Gallery.jsx'
import { getOptimizedImageUrl } from '../lib/imageOptimization.js'
import { generateSchema } from '../lib/seoSchema.js'
import { ALL_REFERENCE_IMAGES as REFERENCE_IMAGES, ALL_CATEGORIES as GALLERY_CATEGORIES } from '../data/galleryData.js'

const SUBPAGE_GALLERY_INFO = {
  'vinduer-og-doere': {
    title: 'Billeder af nymonterede vinduer og døre',
    description: 'Se et udvalg af vores arbejde med udskiftning af facadedøre, terrassedøre og energirigtige vinduer.'
  },
  'traeterasse': {
    title: 'Billeder af vores træterrasser',
    description: 'Gå på opdagelse i vores afsluttede terrasseprojekter – fra hævede terrasser til sommerhusterrasser med trapper og hegn.'
  },
  'gulve-og-lofte': {
    title: 'Billeder af gulvlægning og loftsmontering',
    description: 'Se eksempler på nylagte trægulve, plankegulve og rustikke lofter udført med sans for detaljerne.'
  },
  'hegn-og-stakit': {
    title: 'Billeder af hegn og stakitopsætning',
    description: 'Se eksempler på opsatte havehegn, læhegn og komposithegn monteret med solid forankring.'
  },
  'tagarbejde': {
    title: 'Billeder af tagarbejde og tagrenovering',
    description: 'Se billeder fra vores tagprojekter med nye lægter, undertag, tagsten og metaltag på Mors og i Nordjylland.'
  },
  'renovering': {
    title: 'Billeder fra renovering og facadebeklædning',
    description: 'Se eksempler på husrenoveringer ind- og udvendigt samt nye facadebeklædninger og gavlpartier.'
  },
  'nybyg-og-tilbygning': {
    title: 'Billeder fra nybyg og tilbygninger',
    description: 'Se opførte tilbygninger, tagkonstruktioner, moderne træfacader og integrerede terrasser.'
  },
  'toemrerarbejde': {
    title: 'Billeder fra vores tømrerarbejde',
    description: 'Et bredt udpluk af vores håndværk inden for terrasser, tag, vinduer, gulve og hegn.'
  },
  'om-os': {
    title: 'Eksempler på vores håndværk og kvalitet',
    description: 'Vi sætter en ære i veludført arbejde, gode materialer og tilfredse kunder på Mors og i Limfjordsområdet.'
  }
}

const SLIDESHOW_IMAGES = [
  'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/18c91137-47e1-44a6-875c-4c2b6e19527b.jpeg',
  'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/b4ab8024-08cd-4e16-a7ae-9426ce5e1142.jpeg',
  'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/acec50af-c6d9-43b4-9ae6-cc573d1d49e6.jpeg',
  'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/528aace0-f988-49e0-b5a8-2514079974cb.jpeg',
  'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/fc63893c-8678-4934-80b3-43572afbba0e.jpeg'
]

// Helper function to parse FAQ from HTML content
function parseFaqAndContent(html) {
  // Find any h2 that starts with "Spørgsmål og svar" or "Overvejelser i forbindelse med"
  const faqMatch = html.match(/<h2>(Spørgsmål og svar|Overvejelser i forbindelse med)[^<]*<\/h2>/i)
  if (!faqMatch) {
    return { mainHtml: html, faqItems: null, faqTitle: null }
  }

  const faqHeaderIndex = faqMatch.index
  const faqHeaderTag = faqMatch[0]
  const faqTitle = faqHeaderTag.replace(/<\/?h2>/gi, '')

  const mainHtml = html.substring(0, faqHeaderIndex).replace(/<hr class="my-8 border-stone-200" \/>\s*$/, '')
  const faqHtml = html.substring(faqHeaderIndex + faqHeaderTag.length)

  const faqItems = []
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(faqHtml, 'text/html')
    const h3s = doc.querySelectorAll('h3')
    
    h3s.forEach((h3) => {
      const question = h3.textContent
      let answerHtml = ''
      let nextSibling = h3.nextElementSibling
      
      while (nextSibling && nextSibling.tagName !== 'H3') {
        answerHtml += nextSibling.outerHTML
        nextSibling = nextSibling.nextElementSibling
      }
      
      faqItems.push({
        question,
        answerHtml
      })
    })
  } catch (e) {
    console.error('Error parsing FAQ HTML:', e)
  }

  return { mainHtml, faqItems, faqTitle }
}

export default function PageTemplate({ pageKey }) {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [activeCategory, setActiveCategory] = useState('Alle')
  const [lightboxIndex, setLightboxIndex] = useState(null)

  const filteredImages = activeCategory === 'Alle'
    ? REFERENCE_IMAGES
    : REFERENCE_IMAGES.filter(img => img.category === activeCategory)

  const subpageGalleryMeta = SUBPAGE_GALLERY_INFO[pageKey]

  const openLightbox = (index) => {
    setLightboxIndex(index)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxIndex(null)
    document.body.style.overflow = 'unset'
  }

  const navigateLightbox = (direction, e) => {
    if (e) e.stopPropagation()
    if (filteredImages.length === 0) return

    if (direction === 'next') {
      setLightboxIndex((prev) => (prev + 1) % filteredImages.length)
    } else {
      setLightboxIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length)
    }
  }

  // Handle keyboard navigation inside lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') navigateLightbox('next')
      if (e.key === 'ArrowLeft') navigateLightbox('prev')
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxIndex, filteredImages])

  // CMS states for dynamic homepage sections & competencies
  const [forsideSektioner, setForsideSektioner] = useState(null)
  const [kernekompetencer, setKernekompetencer] = useState(null)

  useEffect(() => {
    let isMounted = true

    const loadCmsData = async () => {
      try {
        const [resSektioner, resKompetencer] = await Promise.all([
          fetch('/_api/public/content/forside_sektioner'),
          fetch('/_api/public/content/kernekompetencer')
        ])

        if (resSektioner.ok) {
          const dataSektioner = await resSektioner.json()
          const items = Array.isArray(dataSektioner) ? dataSektioner : (dataSektioner.items || [])
          if (isMounted) {
            const map = {}
            items.forEach(item => {
              if (item.key) map[item.key] = item
            })
            setForsideSektioner(map)
          }
        }

        if (resKompetencer.ok) {
          const dataKompetencer = await resKompetencer.json()
          const items = Array.isArray(dataKompetencer) ? dataKompetencer : (dataKompetencer.items || [])
          if (isMounted && items.length > 0) {
            const sorted = [...items].sort((a, b) => (Number(a.sortering) || 0) - (Number(b.sortering) || 0))
            setKernekompetencer(sorted)
          }
        }
      } catch (e) {
        console.error('Fejl ved hentning af forside CMS-data:', e)
      }
    }

    if (pageKey === 'index') {
      loadCmsData()
    }

    return () => {
      isMounted = false
    }
  }, [pageKey])

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        setLoading(true)
        const response = await fetch('/_api/public/content/sider')
        if (!response.ok) {
          throw new Error('Kunne ikke hente sideindhold')
        }
        const data = await response.json()
        const items = Array.isArray(data) ? data : (data.items || [])
        const matchedPage = items.find(item => item.key === pageKey)
        
        if (matchedPage) {
          setPageData(matchedPage)
        } else {
          throw new Error(`Siden med nøglen "${pageKey}" blev ikke fundet.`)
        }
      } catch (err) {
        console.error('Error fetching page content:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchPageContent()
  }, [pageKey])

  useEffect(() => {
    if (pageKey !== 'index') return
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDESHOW_IMAGES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [pageKey])

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-stone-500">
        <Loader2 className="h-10 w-10 animate-spin text-[#c08f66] mb-3" />
        <p className="text-sm font-medium">Indlæser sideindhold...</p>
      </div>
    )
  }

  if (error || !pageData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-[#1e1e1e] mb-4">Der skete en fejl</h2>
        <p className="text-[#475569] mb-8">{error || 'Siden kunne ikke indlæses.'}</p>
        <a
          href="/"
          className="inline-flex items-center justify-center bg-[#c08f66] text-white font-medium px-6 py-3 rounded-sm hover:bg-[#a6764e] transition-colors duration-200 text-sm"
        >
          Gå til forsiden
        </a>
      </div>
    )
  }

  const title = pageData.title?.da || ''
  const metaTitle = pageData.meta_title?.da || 'Tømrermester Daniel Vandet'
  const metaDescription = pageData.meta_description?.da || ''
  const contentHtml = pageData.content?.da || ''
  const heroImage = pageData.images?.[0] || 'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/b4ab8024-08cd-4e16-a7ae-9426ce5e1142.jpeg'
  const showHeroImage = Boolean(heroImage)

  const { mainHtml, faqItems, faqTitle } = parseFaqAndContent(contentHtml)

  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}`
    : `https://dvbolig.dk/${pageKey === 'index' ? '' : pageKey === 'gulve-og-lofte' ? 'gulve-og-lofter' : pageKey}`

  const schemas = generateSchema({
    pageKey,
    title,
    metaTitle,
    metaDescription,
    canonicalUrl,
    heroImage,
    faqItems,
  })

  const renderHelmet = () => (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Tømrermester Daniel Vandet" />
      <meta property="og:locale" content="da_DK" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={getOptimizedImageUrl(heroImage, { width: 1200, quality: 85 })} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={getOptimizedImageUrl(heroImage, { width: 1200, quality: 85 })} />
      <link rel="canonical" href={canonicalUrl} />
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )

  // If this is the "Se vores arbejde" (referencer) page
  if (pageKey === 'referencer') {
    return (
      <>
        {renderHelmet()}

        {/* Page Header Banner */}
        <section className="relative bg-[#1e1e1e] text-white pt-36 pb-16 sm:pt-44 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            {showHeroImage ? (
              <>
                <img
                  src={getOptimizedImageUrl(heroImage, { width: 1600, quality: 80 })}
                  alt={`${title} - Tømrermester Daniel Vandet`}
                  className="h-full w-full object-cover opacity-30"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-black/50" />
              </>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-b from-[#1e1e1e] to-[#1e1e1e]" />
            )}
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              {title}
            </h1>
            <div className="h-1 w-16 bg-[#c08f66] mx-auto" />
          </div>
        </section>

        {/* Filterable Gallery Section */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {GALLERY_CATEGORIES.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setActiveCategory(category)
                    setLightboxIndex(null)
                  }}
                  className={`px-5 py-2.5 rounded-sm text-sm font-semibold transition-all duration-200 cursor-pointer ${
                    activeCategory === category
                      ? 'bg-[#c08f66] text-white shadow-sm'
                      : 'bg-[#f6f6f4] text-[#1e1e1e] hover:bg-stone-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Filtered Grid of Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((img, index) => (
                <div
                  key={img.url}
                  onClick={() => openLightbox(index)}
                  className="relative group overflow-hidden rounded-sm cursor-pointer border border-stone-200 shadow-sm aspect-[4/3]"
                >
                  <img
                    src={getOptimizedImageUrl(img.url, { width: 600, quality: 80 })}
                    alt={img.alt}
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-[#c08f66]/80 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center p-4 text-center transition-all duration-300">
                    <span className="text-white font-bold text-lg tracking-wide mb-1">
                      {img.category}
                    </span>
                    <span className="text-white/90 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                      Vis billede <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Lightbox Overlay */}
            {lightboxIndex !== null && (
              <div
                onClick={closeLightbox}
                className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/95 p-4 animate-in fade-in duration-200"
              >
                {/* Close Button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-50 rounded-full bg-stone-900/80 p-3 text-white hover:bg-stone-800 transition-colors cursor-pointer"
                  aria-label="Luk stor visning"
                >
                  <X className="h-6 w-6" />
                </button>

                {/* Navigation Buttons */}
                {filteredImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => navigateLightbox('prev', e)}
                      className="absolute left-4 z-50 rounded-full bg-stone-900/80 p-3 text-white hover:bg-stone-800 transition-colors cursor-pointer"
                      aria-label="Forrige billede"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      onClick={(e) => navigateLightbox('next', e)}
                      className="absolute right-4 z-50 rounded-full bg-stone-900/80 p-3 text-white hover:bg-stone-800 transition-colors cursor-pointer"
                      aria-label="Næste billede"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  </>
                )}

                {/* Image Container */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="relative max-h-[90vh] max-w-[95vw] overflow-hidden rounded-sm"
                >
                  <img
                    src={getOptimizedImageUrl(filteredImages[lightboxIndex].url, { width: 1600, quality: 85 })}
                    alt={filteredImages[lightboxIndex].alt}
                    className="max-h-[85vh] max-w-full object-contain mx-auto"
                    decoding="async"
                  />
                  {/* Image Counter & Category */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-stone-900/85 text-white text-xs px-4 py-2 rounded-full flex items-center gap-2 font-medium">
                    <span className="text-[#c08f66] font-semibold">{filteredImages[lightboxIndex].category}</span>
                    <span className="text-stone-400">|</span>
                    <span>{lightboxIndex + 1} / {filteredImages.length}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </>
    )
  }

  // If this is the Home page (index)
  if (pageKey === 'index') {
    return (
      <>
        {renderHelmet()}

        {/* Hero Section */}
        <section className="relative bg-[#1e1e1e] text-white overflow-hidden min-h-screen flex items-center">
          {/* Background Image Slideshow with Overlay */}
          <div className="absolute inset-0 z-0">
            {SLIDESHOW_IMAGES.map((imgUrl, index) => {
              const slideAlts = [
                'Tømrermester Daniel Vandet - Professionelt tagarbejde og tagkonstruktion',
                'Tømrermester Daniel Vandet - Bygning af ny træterrasse og udendørs træarbejde',
                'Tømrermester Daniel Vandet - Kvalitetsbevidst tømrerarbejde og renovering',
                'Tømrermester Daniel Vandet - Husrenovering og tømreropgaver på Mors, i Thy, Skive og Roslev',
                'Tømrermester Daniel Vandet - Solidt håndværk, træterrasser og tagarbejde'
              ]
              return (
                <img
                  key={imgUrl}
                  src={getOptimizedImageUrl(imgUrl, { width: 1920, quality: 80 })}
                  alt={slideAlts[index] || `Tømrermester Daniel Vandet tømrerarbejde ${index + 1}`}
                  className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-1000 ease-in-out ${
                    index === currentSlide ? 'opacity-60' : 'opacity-0'
                  }`}
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              )
            })}
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/30" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-20 md:pt-48 md:pb-32">
            <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-semibold bg-[#c08f66] text-white mb-4 uppercase tracking-wider">
                {forsideSektioner?.hero?.undertitel?.da || forsideSektioner?.hero?.undertitel || 'Tømrerfirma fra Roslev'}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-6 leading-tight">
                {forsideSektioner?.hero?.overskrift?.da || forsideSektioner?.hero?.overskrift || (
                  <>
                    Tømrermester<br />
                    <span className="text-[#c08f66]">Daniel Vandet</span>
                  </>
                )}
              </h1>
              {forsideSektioner?.hero?.tekst?.da ? (
                <div 
                  className="text-lg sm:text-xl text-stone-200 mb-8 leading-relaxed max-w-2xl mx-auto"
                  dangerouslySetInnerHTML={{ __html: forsideSektioner.hero.tekst.da }}
                />
              ) : (
                <p className="text-lg sm:text-xl text-stone-200 mb-8 leading-relaxed max-w-2xl mx-auto">
                  Tømrermester Daniel Vandet kan hjælpe med opgaver i alle størrelse.
                </p>
              )}
              
              {/* Bullet Points */}
              <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-10 max-w-2xl">
                <div className="flex items-center gap-2 text-stone-200 text-sm md:text-base font-medium">
                  <CheckCircle2 className="h-5 w-5 text-[#c08f66] shrink-0" />
                  <span>Mere end 10 års erfaring</span>
                </div>
                <div className="flex items-center gap-2 text-stone-200 text-sm md:text-base font-medium">
                  <CheckCircle2 className="h-5 w-5 text-[#c08f66] shrink-0" />
                  <span>Vi kan hjælpe med alle opgaver</span>
                </div>
                <div className="flex items-center gap-2 text-stone-200 text-sm md:text-base font-medium">
                  <CheckCircle2 className="h-5 w-5 text-[#c08f66] shrink-0" />
                  <span>Konkurrencedygtige priser</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href={forsideSektioner?.hero?.knap_link || "tel:+4520532767"}
                  className="inline-flex items-center justify-center bg-[#c08f66] hover:bg-[#a6764e] text-white font-semibold px-8 py-4 rounded-sm shadow-sm transition-all duration-200 text-sm uppercase tracking-wider cursor-pointer whitespace-nowrap"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  <span>{forsideSektioner?.hero?.knap_tekst?.da || forsideSektioner?.hero?.knap_tekst || 'Ring op'}</span>
                </a>
                <a
                  href="mailto:dvbolig@hotmail.com"
                  className="inline-flex items-center justify-center border border-white text-white font-semibold px-8 py-4 rounded-sm bg-transparent hover:bg-white hover:text-[#1e1e1e] transition-all duration-200 text-sm uppercase tracking-wider cursor-pointer whitespace-nowrap"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  <span>Send e-mail</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Kernekompetencer / Bento Style Mosaic Grid Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-sm font-semibold tracking-wide uppercase text-[#c08f66]">
                Vores specialer
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e1e1e] tracking-tight mt-2">
                Kernekompetencer inden for tømrerarbejde
              </h2>
              <p className="text-base font-normal leading-relaxed text-[#475569] mt-4">
                Vi tilbyder en bred vifte af kompetencer inden for tømrer- og snedkerarbejde. Uanset projektets størrelse leverer vi altid et resultat i højeste kvalitet.
              </p>
            </div>

            {/* Bento Style Mosaic Grid Layout */}
            {kernekompetencer && kernekompetencer.length >= 4 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Column 1: Tall Portrait Card */}
                <a
                  href={kernekompetencer[0].link}
                  className="relative overflow-hidden group rounded-sm cursor-pointer h-[400px] lg:h-[600px] shadow-md block"
                >
                  <img
                    src={getOptimizedImageUrl(kernekompetencer[0].billede?.[0], { width: 800, quality: 80 })}
                    alt={kernekompetencer[0].titel?.da || kernekompetencer[0].titel}
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                  <div className="absolute bottom-6 left-6 text-white z-10">
                    <h3 className="text-2xl font-bold tracking-wide">{kernekompetencer[0].titel?.da || kernekompetencer[0].titel}</h3>
                    <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                      Læs mere <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </a>

                {/* Column 2: Stacked Sub-grid */}
                <div className="flex flex-col gap-6">
                  {/* Top Row: Full width wide horizontal card */}
                  <a
                    href={kernekompetencer[1].link}
                    className="relative overflow-hidden group rounded-sm cursor-pointer h-[288px] shadow-md block"
                  >
                    <img
                      src={getOptimizedImageUrl(kernekompetencer[1].billede?.[0], { width: 800, quality: 80 })}
                      alt={kernekompetencer[1].titel?.da || kernekompetencer[1].titel}
                      className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                    <div className="absolute bottom-6 left-6 text-white z-10">
                      <h3 className="text-xl font-bold tracking-wide">{kernekompetencer[1].titel?.da || kernekompetencer[1].titel}</h3>
                      <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                        Læs mere <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </a>

                  {/* Bottom Row: 2-column inline nested grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 h-auto lg:h-[288px]">
                    {/* Card 3 */}
                    <a
                      href={kernekompetencer[2].link}
                      className="relative overflow-hidden group rounded-sm cursor-pointer h-[200px] sm:h-full shadow-md block"
                    >
                      <img
                        src={getOptimizedImageUrl(kernekompetencer[2].billede?.[0], { width: 600, quality: 80 })}
                        alt={kernekompetencer[2].titel?.da || kernekompetencer[2].titel}
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                      <div className="absolute bottom-6 left-6 text-white z-10">
                        <h3 className="text-lg font-bold tracking-wide">{kernekompetencer[2].titel?.da || kernekompetencer[2].titel}</h3>
                        <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                          Læs mere <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </a>

                    {/* Card 4 */}
                    <a
                      href={kernekompetencer[3].link}
                      className="relative overflow-hidden group rounded-sm cursor-pointer h-[200px] sm:h-full shadow-md block"
                    >
                      <img
                        src={getOptimizedImageUrl(kernekompetencer[3].billede?.[0], { width: 600, quality: 80 })}
                        alt={kernekompetencer[3].titel?.da || kernekompetencer[3].titel}
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                      <div className="absolute bottom-6 left-6 text-white z-10">
                        <h3 className="text-lg font-bold tracking-wide">{kernekompetencer[3].titel?.da || kernekompetencer[3].titel}</h3>
                        <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                          Læs mere <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Column 1: Tall Portrait Card */}
                <a
                  href="/tagarbejde"
                  className="relative overflow-hidden group rounded-sm cursor-pointer h-[400px] lg:h-[600px] shadow-md block"
                >
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/18c91137-47e1-44a6-875c-4c2b6e19527b.jpeg', { width: 800, quality: 80 })}
                    alt="Tagarbejde og tagrenovering udført af Tømrermester Daniel Vandet"
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                  <div className="absolute bottom-6 left-6 text-white z-10">
                    <h3 className="text-2xl font-bold tracking-wide">Tagarbejde</h3>
                    <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                      Læs mere <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </a>

                {/* Column 2: Stacked Sub-grid */}
                <div className="flex flex-col gap-6">
                  {/* Top Row: Full width wide horizontal card */}
                  <a
                    href="/renovering"
                    className="relative overflow-hidden group rounded-sm cursor-pointer h-[288px] shadow-md block"
                  >
                    <img
                      src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/2c8bed7f-0b1c-43ab-b5a5-79114a51b482.png', { width: 800, quality: 80 })}
                      alt="Renovering af hus og ombygning ved Tømrermester Daniel Vandet"
                      className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                    <div className="absolute bottom-6 left-6 text-white z-10">
                      <h3 className="text-xl font-bold tracking-wide">Renovering af hus</h3>
                      <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                        Læs mere <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </a>

                  {/* Bottom Row: 2-column inline nested grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 h-auto lg:h-[288px]">
                    {/* Card 3: Vinduer & døre */}
                    <a
                      href="/vinduer-og-doere"
                      className="relative overflow-hidden group rounded-sm cursor-pointer h-[200px] sm:h-full shadow-md block"
                    >
                      <img
                        src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/2a292ba4-7b73-4250-a254-8cb2297b35fd.jpeg', { width: 600, quality: 80 })}
                        alt="Udskiftning og montering af vinduer og døre"
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                      <div className="absolute bottom-6 left-6 text-white z-10">
                        <h3 className="text-lg font-bold tracking-wide">Udskiftning af vinduer og døre</h3>
                        <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                          Læs mere <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </a>

                    {/* Card 4: Træterrasse */}
                    <a
                      href="/traeterasse"
                      className="relative overflow-hidden group rounded-sm cursor-pointer h-[200px] sm:h-full shadow-md block"
                    >
                      <img
                        src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/b4ab8024-08cd-4e16-a7ae-9426ce5e1142.jpeg', { width: 600, quality: 80 })}
                        alt="Opførelse af træterrasse i høj kvalitet"
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-[#c08f66]/80" />
                      <div className="absolute bottom-6 left-6 text-white z-10">
                        <h3 className="text-lg font-bold tracking-wide">Træterasse</h3>
                        <span className="text-sm font-medium text-white/0 group-hover:text-white/100 transition-opacity duration-300 flex items-center gap-1 mt-2">
                          Læs mere <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Alternating Content Block 1: Text Left, Image Right */}
        <section className="py-16 md:py-24 bg-white border-t border-stone-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left Column: Content */}
              <div className="space-y-6">
                <span className="text-sm font-semibold tracking-wide uppercase text-[#c08f66]">
                  {forsideSektioner?.lokal_toemrer?.undertitel?.da || forsideSektioner?.lokal_toemrer?.undertitel || 'Din lokale tømrer'}
                </span>
                <h2 className="text-3xl font-bold text-[#1e1e1e] tracking-tight">
                  {forsideSektioner?.lokal_toemrer?.overskrift?.da || forsideSektioner?.lokal_toemrer?.overskrift || 'Tømrer klar til at hjælpe i Thy, Roslev, Skive og Mors'}
                </h2>
                {forsideSektioner?.lokal_toemrer?.tekst?.da ? (
                  <div
                    className="text-base font-normal leading-relaxed text-[#475569] space-y-4"
                    dangerouslySetInnerHTML={{ __html: forsideSektioner.lokal_toemrer.tekst.da }}
                  />
                ) : (
                  <p className="text-base font-normal leading-relaxed text-[#475569]">
                    Som din lokale tømrer i Roslev tilbyder vi a bred vifte af kompetencer inden for tømrer- og snedkerarbejde. Uanset om du skal have bygget en ny terrasse, skiftet vinduer eller lavet en totalrenovering, står vi klar med solide løsninger og et skarpt øje for detaljer. Kontakt os i dag og hør mere om, hvordan vi kan hjælpe dig med dit næste projekt!
                  </p>
                )}
                <div className="pt-4">
                  <a
                    href={forsideSektioner?.lokal_toemrer?.knap_link || "/toemrerarbejde"}
                    className="inline-flex items-center justify-center bg-[#c08f66] hover:bg-[#a6764e] text-white font-semibold px-6 py-3 rounded-sm shadow-sm transition-all duration-200 text-sm uppercase tracking-wider cursor-pointer whitespace-nowrap"
                  >
                    {forsideSektioner?.lokal_toemrer?.knap_tekst?.da || forsideSektioner?.lokal_toemrer?.knap_tekst || 'Læs mere om vores tømrerarbejde'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </a>
                </div>
              </div>

              {/* Right Column: Image */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-sm overflow-hidden border border-stone-200 shadow-lg bg-stone-100">
                  <img
                    src={getOptimizedImageUrl(
                      forsideSektioner?.lokal_toemrer?.billeder?.[0] || 'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/acec50af-c6d9-43b4-9ae6-cc573d1d49e6.jpeg',
                      { width: 800, quality: 80 }
                    )}
                    alt="Tømrermester Daniel Vandet udfører tømrerarbejde i Thy, Roslev, Skive og Mors"
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Alternating Content Block 2: Image Left, Text Right (Light Gray Background) */}
        <section className="py-16 md:py-24 bg-[#f6f6f4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left Column: Image with Badge */}
              <div className="relative order-last lg:order-first">
                <div className="aspect-[4/3] rounded-sm overflow-hidden border border-stone-200 shadow-lg bg-stone-100">
                  <img
                    src={getOptimizedImageUrl(
                      forsideSektioner?.ydelser_intro?.billeder?.[0] || 'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/4bfd208b-c049-43cf-9d69-ee5a8813b178.jpeg',
                      { width: 800, quality: 80 }
                    )}
                    alt="Håndværk og renovering med mere end 10 års erfaring - Tømrermester Daniel Vandet"
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-6 -right-6 bg-[#c08f66] text-white p-6 rounded-sm shadow-xl max-w-xs hidden sm:block">
                  <p className="text-3xl font-extrabold text-white">10+</p>
                  <p className="text-sm font-semibold mt-1">Års erfaring med tømrer- og snedkerarbejde</p>
                </div>
              </div>

              {/* Right Column: Content */}
              <div className="space-y-6">
                <span className="text-sm font-semibold tracking-wide uppercase text-[#c08f66]">
                  {forsideSektioner?.ydelser_intro?.undertitel?.da || forsideSektioner?.ydelser_intro?.undertitel || 'Vores ydelser'}
                </span>
                <h2 className="text-3xl font-bold text-[#1e1e1e] tracking-tight">
                  {forsideSektioner?.ydelser_intro?.overskrift?.da || forsideSektioner?.ydelser_intro?.overskrift || 'Professionelt tagarbejde & husrenovering'}
                </h2>
                
                {forsideSektioner?.ydelser_intro?.tekst?.da ? (
                  <div
                    className="space-y-6 pt-2 text-base font-normal leading-relaxed text-[#475569] prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: forsideSektioner.ydelser_intro.tekst.da }}
                  />
                ) : (
                  <div className="space-y-6 pt-2">
                    <div>
                      <h3 className="text-lg font-bold text-[#1e1e1e] flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-[#c08f66]" />
                        Tagarbejde
                      </h3>
                      <p className="text-base font-normal leading-relaxed text-[#475569] mt-1">
                        Hos Tømrermester Daniel Vandet får du professionelt og påliveligt tagarbejde i Thy, Roslev, Skive og Mors. Vi er specialister i alt fra tagrenovering og tagudskiftning til mindre reparationer, og vi leverer altid et resultat i højeste kvalitet.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-[#1e1e1e] flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-[#c08f66]" />
                        Renovering
                      </h3>
                      <p className="text-base font-normal leading-relaxed text-[#475569] mt-1">
                        Tømrermester Daniel Vandet tilbyder professionel husrenovering og ombygning i Thy, Roslev, Skive og Mors. Med stor erfaring og øje for detaljer hjælper vi dig med at skabe den bolig, du drømmer om.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Billedsektion med egne billeder */}
        <section className="py-16 md:py-24 bg-white border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-sm font-semibold tracking-wide uppercase text-[#c08f66]">
                Vores arbejde
              </span>
              <h2 className="text-3xl font-bold text-[#1e1e1e] tracking-tight mt-2">
                Se vores tidligere projekter
              </h2>
              <p className="text-base font-normal leading-relaxed text-[#475569] mt-4">
                Her er et lille udvalg af vores tidligere projekter. Vi sætter en ære i at levere et flot og holdbart resultat hver gang.
              </p>
            </div>

            <Gallery galleryKey="referencer" limit={6} />

            <div className="text-center mt-12">
              <a
                href="/referencer"
                className="inline-flex items-center justify-center bg-[#c08f66] hover:bg-[#a6764e] text-white font-semibold px-6 py-3 rounded-sm shadow-sm transition-all duration-200 text-sm uppercase tracking-wider cursor-pointer whitespace-nowrap"
              >
                Se vores arbejde
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </div>
          </div>
        </section>

        {/* Kontaktsektion */}
        <section className="relative py-16 md:py-24 bg-[#1e1e1e] overflow-hidden" id="kontakt-sektion">
          {/* Background Image with Gradient Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src={getOptimizedImageUrl(
                forsideSektioner?.kontakt_sektion?.billeder?.[0] || 'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/acec50af-c6d9-43b4-9ae6-cc573d1d49e6.jpeg',
                { width: 1400, quality: 75 }
              )}
              alt="Kontakt Tømrermester Daniel Vandet for et uforpligtende tilbud på tømrerarbejde"
              className="h-full w-full object-cover object-center"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#1e1e1e]/75 to-[#1e1e1e]" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Contact Info */}
              <div className="lg:col-span-5 space-y-8">
                <div>
                  <span className="text-sm font-semibold tracking-wide uppercase text-[#c08f66]">
                    {forsideSektioner?.kontakt_sektion?.undertitel?.da || forsideSektioner?.kontakt_sektion?.undertitel || 'Kontakt os i dag'}
                  </span>
                  <h2 className="text-3xl font-bold text-white tracking-tight mt-2">
                    {forsideSektioner?.kontakt_sektion?.overskrift?.da || forsideSektioner?.kontakt_sektion?.overskrift || 'Kontakt din lokale tømrer fra Thy, Roslev, Skive og Mors'}
                  </h2>
                </div>

                {forsideSektioner?.kontakt_sektion?.tekst?.da ? (
                  <div
                    className="text-base font-normal leading-relaxed text-stone-300 space-y-4"
                    dangerouslySetInnerHTML={{ __html: forsideSektioner.kontakt_sektion.tekst.da }}
                  />
                ) : (
                  <>
                    <p className="text-base font-normal leading-relaxed text-stone-300">
                      Har du brug for en pålivelig og erfaren tømrer i Thy, Skive, Mors eller Roslev? Tømrermester Daniel Vandet is din lokale ekspert i alt fra små reparationer til større byggeprojekter. Vi tilbyder en bred vifte af tømreropgaver, herunder tagarbejde, husrenovering, ombygning og specialopgaver, der kræver fagligt overblik og høj kvalitet.
                    </p>
                    <p className="text-base font-normal leading-relaxed text-stone-300">
                      Vi sætter en ære i at levere solidt håndværk og løsninger, der er skræddersyet til dine behov. Med vores professionelle tilgang og mange års erfaring er du garanteret et resultat, der både er holdbart og æstetisk.
                    </p>
                  </>
                )}
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </>
    )
  }

  // General Layout for all other pages
  return (
    <>
      {renderHelmet()}

      {/* Page Header Banner */}
      <section className="relative bg-[#1e1e1e] text-white pt-36 pb-16 sm:pt-44 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {showHeroImage ? (
            <>
              <img
                src={getOptimizedImageUrl(heroImage, { width: 1600, quality: 80 })}
                alt={title}
                className="h-full w-full object-cover opacity-30"
                fetchPriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-black/50" />
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-b from-[#1e1e1e] to-[#1e1e1e]" />
          )}
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
            {title}
          </h1>
          <div className="h-1 w-16 bg-[#c08f66] mx-auto" />
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Rich Text Content & Gallery */}
            <div className="lg:col-span-7 space-y-12">
              {pageKey === 'kontakt' ? (
                <div className="space-y-10">
                  <p className="text-base font-normal leading-relaxed text-[#475569]">
                    Vi står klar til at hjælpe dig! Har du spørgsmål eller brug for et uforpligtende tilbud? Kontakt os i dag – vi er her for at gøre dine projekter til virkelighed.
                  </p>
                  
                  {/* Contact List Container */}
                  <div className="flex flex-col gap-y-10 bg-white p-8 sm:p-10 rounded-sm border border-stone-200 shadow-sm">
                    {/* Item: Telefon */}
                    <div className="flex items-start gap-x-8">
                      {/* Icon Container */}
                      <div className="w-10 flex justify-center text-[#c08f66] pt-1 shrink-0">
                        <Phone className="w-7 h-7" strokeWidth={2.5} />
                      </div>
                      {/* Text Block */}
                      <div className="flex flex-col gap-y-1">
                        <h3 className="text-xl font-bold text-[#c08f66] m-0">Telefon:</h3>
                        <a href="tel:+4520532767" className="text-lg font-semibold text-[#1e1e1e] hover:text-[#c08f66] transition-colors whitespace-nowrap">
                          +45 20 53 27 67
                        </a>
                      </div>
                    </div>

                    {/* Item: Email */}
                    <div className="flex items-start gap-x-8">
                      <div className="w-10 flex justify-center text-[#c08f66] pt-1 shrink-0">
                        <Mail className="w-7 h-7" strokeWidth={2.5} />
                      </div>
                      <div className="flex flex-col gap-y-1">
                        <h3 className="text-xl font-bold text-[#c08f66] m-0">Email:</h3>
                        <a href="mailto:dvbolig@hotmail.com" className="text-lg font-semibold text-[#1e1e1e] hover:text-[#c08f66] transition-colors break-all">
                          dvbolig@hotmail.com
                        </a>
                      </div>
                    </div>

                    {/* Item: Adresse */}
                    <div className="flex items-start gap-x-8">
                      <div className="w-10 flex justify-center text-[#c08f66] pt-1 shrink-0">
                        <MapPin className="w-7 h-7" strokeWidth={2.5} />
                      </div>
                      <div className="flex flex-col gap-y-1">
                        <h3 className="text-xl font-bold text-[#c08f66] m-0">Adresse:</h3>
                        <p className="text-lg font-semibold text-[#1e1e1e] m-0 leading-snug">
                          Lyngvænget 73, 7870 Glyngøre, Denmark
                        </p>
                      </div>
                    </div>

                    {/* Item: CVR */}
                    <div className="flex items-start gap-x-8">
                      <div className="w-10 flex justify-center text-[#c08f66] pt-1 shrink-0">
                        <Briefcase className="w-7 h-7" strokeWidth={2.5} />
                      </div>
                      <div className="flex flex-col gap-y-1">
                        <h3 className="text-xl font-bold text-[#c08f66] m-0">CVR-nr:</h3>
                        <p className="text-lg font-semibold text-[#1e1e1e] m-0">
                          40509496
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="max-w-none text-base font-normal leading-relaxed text-[#475569] space-y-6 
                    [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-[#1e1e1e] [&_h1]:mt-8 [&_h1]:mb-4 [&_h1]:tracking-tight
                    [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#1e1e1e] [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:tracking-tight [&_h2]:border-b [&_h2]:border-stone-200 [&_h2]:pb-2
                    [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#1e1e1e] [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:tracking-tight
                    [&_h4]:text-lg [&_h4]:font-bold [&_h4]:text-[#1e1e1e] [&_h4]:mt-4 [&_h4]:mb-2
                    [&_p]:text-base [&_p]:leading-relaxed [&_p]:text-[#475569] [&_p]:mb-4
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1 [&_ul]:text-[#475569]
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-1 [&_ol]:text-[#475569]
                    [&_li]:text-base [&_li]:leading-relaxed [&_li]:mb-2
                    [&_strong]:font-bold [&_strong]:text-[#1e1e1e]
                    [&_em]:italic
                    [&_u]:underline
                    [&_a]:text-[#c08f66] [&_a]:underline [&_a]:hover:text-[#a6764e] [&_a]:transition-colors
                    [&_blockquote]:border-l-4 [&_blockquote]:border-[#c08f66] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#334155]
                    [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_table]:my-6
                    [&_th]:border [&_th]:border-stone-200 [&_th]:bg-stone-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[#1e1e1e]
                    [&_td]:border [&_td]:border-stone-200 [&_td]:px-3 [&_td]:py-2
                    [&_.contact-info]:rounded-sm [&_.contact-info]:border [&_.contact-info]:border-stone-200 [&_.contact-info]:bg-[#f6f6f4] [&_.contact-info]:p-5
                    [&_.contact-info_h3]:mt-0 [&_.contact-info_h3]:text-xl [&_.contact-info_h3]:font-bold [&_.contact-info_h3]:text-[#1e1e1e]
                    [&_.contact-info_p]:mb-0 [&_.contact-info_p]:text-[#475569]
                    [&_.contact-info_a]:font-semibold [&_.contact-info_a]:text-[#1e1e1e]"
                  dangerouslySetInnerHTML={{ __html: mainHtml }}
                />
              )}

              {/* FAQ Accordions */}
              {faqItems && faqItems.length > 0 && (
                <div className="pt-8 border-t border-stone-200 space-y-6">
                  <h2 className="text-2xl font-bold text-[#1e1e1e] tracking-tight border-b border-stone-200 pb-2">
                    {faqTitle || 'Spørgsmål og svar'}
                  </h2>
                  <div className="space-y-4">
                    {faqItems.map((item, index) => (
                      <details
                        key={index}
                        className="group rounded-sm border border-stone-200 bg-white shadow-sm overflow-hidden"
                        open={index === 0}
                      >
                        <summary className="list-none cursor-pointer w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors duration-200 hover:bg-stone-50 [&::-webkit-details-marker]:hidden">
                          <span className="text-base font-semibold text-[#1e1e1e] tracking-tight">
                            {item.question}
                          </span>
                          <ChevronDown className="h-5 w-5 text-[#c08f66] shrink-0 transition-transform duration-300 group-open:rotate-180" />
                        </summary>
                        <div className="border-t border-stone-200 px-5 py-4 text-base leading-relaxed text-[#475569] space-y-4">
                          <div
                            className="space-y-4
                              [&_p]:text-base [&_p]:leading-relaxed [&_p]:text-[#475569] [&_p]:mb-4 [&_p:last-child]:mb-0
                              [&_strong]:font-bold [&_strong]:text-[#1e1e1e]"
                            dangerouslySetInnerHTML={{ __html: item.answerHtml }}
                          />
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* Left Column Content End */}
            </div>

            {/* Right Column: Sticky Contact Sidebar */}
            <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-8">
              {/* Full Contact Form */}
              <ContactForm />

              {pageKey !== 'kontakt' && (
                /* Quick Contact Box */
                <div className="bg-[#1e1e1e] text-white rounded-sm p-6 sm:p-8 shadow-xl border border-white/10">
                  <h3 className="text-xl font-bold mb-4 tracking-tight">Har du brug for hjælp?</h3>
                  <p className="text-stone-300 text-sm leading-relaxed mb-6">
                    Ring eller skriv til os i dag. Vi står klar med rådgivning og et uforpligtende tilbud tilpasset dit projekt.
                  </p>
                  <div className="space-y-4">
                    <a
                      href="tel:+4520532767"
                      className="flex items-center gap-3 p-4 rounded-sm bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 border border-white/10">
                        <Phone className="h-4 w-4 text-[#c08f66]" />
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-stone-300 uppercase tracking-wider font-semibold">Ring direkte</p>
                        <p className="font-bold whitespace-nowrap text-sm sm:text-base">+45 20 53 27 67</p>
                      </div>
                    </a>

                    <a
                      href="mailto:dvbolig@hotmail.com"
                      className="flex items-center gap-3 p-4 rounded-sm bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 border border-white/10">
                        <Mail className="h-4 w-4 text-[#c08f66]" />
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-stone-300 uppercase tracking-wider font-semibold">Send e-mail</p>
                        <p className="font-bold whitespace-nowrap text-sm sm:text-base">dvbolig@hotmail.com</p>
                      </div>
                    </a>

                    <div className="flex items-center gap-3 p-4 rounded-sm bg-white/5 border border-white/10">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 border border-white/10">
                        <MapPin className="h-4 w-4 text-[#c08f66]" />
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-stone-300 uppercase tracking-wider font-semibold">Adresse</p>
                        <p className="font-bold text-sm sm:text-base leading-snug">Lyngvænget 73, 7870 Glyngøre, Denmark</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Dedikeret Karrusel-galleri i bunden af undersiderne */}
      {subpageGalleryMeta && (
        <section className="py-16 sm:py-20 bg-stone-50 border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div>
                <span className="text-xs sm:text-sm font-semibold tracking-wider uppercase text-[#c08f66]">
                  Referencebilleder
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1e1e1e] tracking-tight mt-1">
                  {subpageGalleryMeta.title}
                </h2>
                <p className="text-sm sm:text-base text-[#475569] mt-2 max-w-2xl">
                  {subpageGalleryMeta.description}
                </p>
              </div>
              <div className="shrink-0">
                <a
                  href="/referencer"
                  className="inline-flex items-center text-sm font-semibold text-[#c08f66] hover:text-[#a6764e] transition-colors"
                >
                  Se alle vores referencer <ArrowRight className="w-4 h-4 ml-1.5" />
                </a>
              </div>
            </div>

            <Gallery galleryKey={pageKey} layout="carousel" />
          </div>
        </section>
      )}

      {pageKey === 'kontakt' && (
        <section className="pb-0 bg-white">
          <div className="w-full px-0">
            <div className="overflow-hidden border-y border-stone-200 shadow-lg min-h-[420px] rounded-none">
              <iframe
                title="Kort over Lyngvænget 73, 7870 Glyngøre, Denmark"
                src="https://www.google.com/maps?q=Lyngvænget%2073,%207870%20Glyng%C3%B8re,%20Denmark&output=embed"
                className="h-[420px] w-full sm:h-[520px] lg:h-[640px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>
      )}
    </>
  )
}
